/* ============================================================================
   olmia-cart.js — window.OlmiaCart: single source of truth for the cart,
   backed by the Shopify Cart AJAX API (/cart.js, /cart/add.js, /cart/change.js).
   Consumed by olmia-ui.js (drawer + mini-cart) and olmia-cart-page.js (/cart).

   PRIJSMUUR — dit bestand houdt óók de poort bij, want de poort hoort bij de
   cart-state en niet bij één renderer. /cart.js kent geen metavelden, dus de
   tier-informatie komt uitsluitend van de server:

     • bij het laden van de pagina uit snippets/cart-drawer.liquid
       (data-cart-gates / data-cart-known / data-gate-all / data-gate-copy),
       of op /cart uit sections/main-cart.liquid;
     • bij elke mutatie uit dezelfde ronde: /cart/add.js en /cart/change.js
       krijgen `sections: 'main-cart'` + `sections_url: '/cart'` mee en
       antwoorden met de vers gerenderde sectie, waar de poort weer in staat.
       (Gemeten 2026-08-19: die sectie-id rendert standalone met de defaults uit
       het schema — precies daarom lezen we er ALLEEN de poort uit, want die is
       instelling-onafhankelijk; labels/knopteksten blijven van het snippet.)

   FAIL CLOSED — een variant-id dat noch in de gate-map noch in data-cart-known
   staat, is door de server nog niet beoordeeld en telt tot nader order als
   AFGESCHERMD. Zo kan een regel die buiten de poort om in de wagen komt (de
   deelbare permalink /cart/<variant-id>:1, een tweede tabblad) nooit een bedrag
   tonen; de echte tier wordt op de achtergrond alsnog opgehaald.
   ========================================================================== */
window.OlmiaCart = (function () {
  const T = (k, f) => (window.olmiaT ? window.olmiaT(k, f) : f);
  let state = null;          // latest /cart.js JSON
  const subs = [];

  /* ---------- prijsmuur ---------- */
  /* De sectie-id die de poort opnieuw kan renderen. Zie de kop: we lezen er
     uitsluitend de instelling-onafhankelijke gate-attributen uit. */
  const GATE_SECTION = 'main-cart';
  /* Tier voor een regel die de server nog niet beoordeeld heeft. 'quote' is de
     enige tier die voor ELKE bezoeker klopt: "Prijs op aanvraag" is nooit een
     onwaarheid, terwijl "Log in voor prijzen" een b2b-klant of een quote-only
     product een belofte doet die inloggen niet kan waarmaken. */
  const FAIL_CLOSED = 'quote';
  const FALLBACK_COPY = {
    quote: { label: T('common.price_on_request', 'Prijs op aanvraag'), href: '/pages/contact', cta: T('common.request_quote', 'Offerte aanvragen'), note: '' },
    pending: { label: T('common.price_on_request', 'Prijs op aanvraag'), href: '/pages/contact', cta: T('common.request_quote', 'Offerte aanvragen'), note: '' },
    guest: { label: T('common.login_for_prices', 'Log in voor prijzen'), href: '/account/login', cta: T('cart.gate_cta_login', 'Inloggen'), note: '' }
  };

  let gates = {};        // { '<variant-id>': 'quote' | 'pending' | 'guest' }
  let known = null;      // Set van variant-ids waarover de server een uitspraak deed; null = niets bekend
  let gateAll = '';      // 'guest' zodra de zachte gastenmuur aan staat
  let gateCopy = {};     // labels/hrefs/cta/note per tier
  let resolving = false;
  let resolvedSig = null;   // cart-signatuur waarvoor de poort compleet is
  let attempts = {};        // signatuur -> aantal mislukte ophaalpogingen

  function parseJSON(raw, fallback) {
    if (!raw) return fallback;
    try { return JSON.parse(raw); } catch (e) { return fallback; }
  }
  function idSet(raw) {
    const s = new Set();
    String(raw == null ? '' : raw).split(',').forEach(v => { const t = v.trim(); if (t) s.add(t); });
    return s;
  }
  /* Naam bewust NIET `variantId`: `add(variantId, qty)` heeft een parameter met
     die naam, en een schaduwende helper is precies het soort stille fout dat een
     prijspoort onbruikbaar maakt. */
  function lineVariantId(item) {
    if (!item) return '';
    const v = (item.variant_id != null) ? item.variant_id : item.id;
    return (v == null) ? '' : String(v);
  }
  function signature(cart) {
    return ((cart && cart.items) || []).map(lineVariantId).join(',');
  }

  /* Neemt de poort over van een server-gerenderde node ([data-cart-gates]).
     withCopy=false bij een nágehaalde sectie: die rendert met schema-defaults,
     dus de teksten van het snippet blijven leidend. */
  function adoptGates(node, withCopy) {
    if (!node || !node.getAttribute || node.getAttribute('data-cart-gates') === null) return false;
    gates = parseJSON(node.getAttribute('data-cart-gates'), {}) || {};
    known = idSet(node.getAttribute('data-cart-known'));
    gateAll = node.getAttribute('data-gate-all') || '';
    if (withCopy !== false) {
      const copy = parseJSON(node.getAttribute('data-gate-copy'), null);
      if (copy) gateCopy = copy;
    }
    return true;
  }

  function adoptSectionHtml(html, withCopy) {
    if (typeof html !== 'string' || html.length === 0) return false;
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const node = doc.querySelector('[data-cart-page][data-cart-gates]') ||
                   doc.querySelector('#cartDrawer[data-cart-gates]') ||
                   doc.querySelector('[data-cart-gates]');
      return adoptGates(node, withCopy);
    } catch (e) { return false; }
  }

  /* De tier van één cart-regel. '' = open (bedrag mag). */
  function gateFor(item) {
    const id = lineVariantId(item);
    if (gates[id]) return gates[id];
    // Eerst "server heeft beoordeeld en open gelaten" (muur-uitzondering), pas dan de algemene gastenmuur.
    if (known && known.has(id)) return '';
    if (gateAll) return gateAll;
    return FAIL_CLOSED;
  }

  /* Zwaarste tier over de hele wagen: quote wint; guest en pending sluiten
     elkaar uit voor één bezoeker. '' = de hele wagen mag bedragen tonen. */
  function worstGate(items) {
    let worst = '';
    const list = items || (state && state.items) || [];
    for (let i = 0; i < list.length; i++) {
      const g = gateFor(list[i]);
      if (g === 'quote') return 'quote';
      if (g) worst = g;
    }
    return worst;
  }

  function copyFor(st) {
    return gateCopy[st] || FALLBACK_COPY[st] || FALLBACK_COPY.quote;
  }

  function hasUnknown(cart) {
    const items = (cart && cart.items) || [];
    for (let i = 0; i < items.length; i++) {
      const id = lineVariantId(items[i]);
      if (!gates[id] && !(known && known.has(id))) return true;
    }
    return false;
  }

  /* Haalt de poort alsnog bij de server op. Drie routes, van goedkoop naar zeker:
     de Section Rendering API, en als laatste redmiddel /cart zelf (die pagina
     rendert de poort altijd, ook als de sectie-id ooit verandert). */
  async function fetchGates() {
    try {
      const r = await fetch('/cart?section_id=' + encodeURIComponent(GATE_SECTION), { headers: { 'Accept': 'text/html' } });
      if (r.ok && adoptSectionHtml(await r.text(), false)) return true;
    } catch (e) { /* val door naar de volgende route */ }
    try {
      const r = await fetch('/cart', { headers: { 'Accept': 'text/html' } });
      if (r.ok && adoptSectionHtml(await r.text(), false)) return true;
    } catch (e) { /* niets: de poort blijft dicht */ }
    return false;
  }

  /* Roept fetchGates aan zodra de wagen regels bevat die de server niet beoordeeld
     heeft. Per cart-signatuur hoogstens 3 pogingen, zodat een netwerkstoring geen
     verzoekenlus wordt — en zodat een geslaagde ronde niet steeds opnieuw draait. */
  async function ensureGates() {
    const sig = signature(state);
    if (sig === resolvedSig) return false;
    if (!hasUnknown(state)) { resolvedSig = sig; return false; }
    if ((attempts[sig] || 0) >= 3 || resolving) return false;
    resolving = true;
    let ok = false;
    try { ok = await fetchGates(); } finally { resolving = false; }
    if (ok && !hasUnknown(state)) { resolvedSig = sig; attempts = {}; }
    else attempts[sig] = (attempts[sig] || 0) + 1;
    return ok;
  }

  function notify() { subs.forEach(fn => { try { fn(state); } catch (e) { console.error(e); } }); }

  /* notify + (indien nodig) een tweede notify zodra de poort compleet is. De
     eerste tekenbeurt is dan al veilig (fail closed), de tweede is de waarheid. */
  function notifyResolved() {
    notify();
    ensureGates().then(changed => { if (changed) notify(); });
  }

  async function refresh() {
    try {
      const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      // A gated preview (password wall / theme-dev proxy) answers /cart.js with 401 and
      // an HTML body — keep the last known cart then. NOTE: judge by PARSING, never by
      // content-type: Shopify serves the cart API as `text/javascript`, so a
      // "includes('json')" header check silently drops real cart data (ROADBLOCKS #13).
      if (r.ok) {
        const text = await r.text();
        try { state = JSON.parse(text); } catch (e) { /* HTML body on a gated preview */ }
      }
    } catch (e) { /* network hiccup: keep the last known cart, stay quiet */ }
    notifyResolved();
    return state;
  }

  function subscribe(fn) {
    subs.push(fn);
    if (state) { try { fn(state); } catch (e) { console.error(e); } }
    return fn;
  }

  async function add(variantId, qty = 1) {
    let item = null;
    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        // sections/sections_url: de poort voor het NIEUWE artikel in dezelfde ronde,
        // zodat de lade nooit één tekenbeurt lang een bedrag toont dat niet mag.
        body: JSON.stringify({ items: [{ id: variantId, quantity: qty }], sections: GATE_SECTION, sections_url: '/cart' })
      });
      const data = await r.json();
      if (!r.ok) throw data;
      if (data.sections) adoptSectionHtml(data.sections[GATE_SECTION], false);
      item = (data.items && data.items[0]) || data;
    } catch (e) {
      console.error('add failed', e);
      if (window.OlmiaUI) OlmiaUI.toast(T('common.add_failed', 'Kon niet toevoegen aan winkelwagen'));
      return null;
    }
    await refresh();
    const title = (item && (item.product_title || item.title)) || 'Product';
    if (window.OlmiaUI) OlmiaUI.toast(T('cart.item_added', '{title} toegevoegd').replace('{title}', function () { return title; }));
    open();
    return item;
  }

  async function change(key, qty) {
    try {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: key, quantity: qty, sections: GATE_SECTION, sections_url: '/cart' })
      });
      const data = await r.json();
      if (data && data.sections) {
        adoptSectionHtml(data.sections[GATE_SECTION], false);
        delete data.sections;     // de sectie-HTML hoort niet in de cart-state
      }
      state = data;
    } catch (e) { console.error('change failed', e); }
    notifyResolved();
    return state;
  }

  function open() {
    const d = document.getElementById('cartDrawer');
    if (d) {
      // Reveal to AT + focus order: the closed drawer sits off-screen but stays
      // in the DOM, so it must be `inert` (unfocusable, aria-hidden) while closed
      // and fully interactive while open — otherwise its buttons/links are
      // focusable inside an aria-hidden subtree (axe: aria-hidden-focus).
      d.removeAttribute('inert');
      d.removeAttribute('aria-hidden');
      d.classList.add('open');
    }
    document.getElementById('cartScrim')?.classList.add('open');
    document.body.style.overflow = 'hidden';
  }
  function close() {
    const d = document.getElementById('cartDrawer');
    if (d) {
      d.classList.remove('open');
      d.setAttribute('aria-hidden', 'true');
      d.setAttribute('inert', '');
    }
    document.getElementById('cartScrim')?.classList.remove('open');
    document.body.style.overflow = '';
  }
  function count() { return state ? state.item_count : 0; }

  /* Poort uit de server-HTML overnemen vóór de eerste refresh: de lade staat in
     layout/theme.liquid en is er dus op elke pagina; op /cart draagt de sectie
     dezelfde attributen (mét de door de merchant instelbare teksten). */
  adoptGates(document.getElementById('cartDrawer'), true) ||
    adoptGates(document.querySelector('[data-cart-page][data-cart-gates]'), true);

  refresh();

  return {
    get cart() { return state; },
    get gateAll() { return gateAll; },
    subscribe, add, change, open, close, count, refresh,
    gateFor, worstGate, copyFor, adoptGates, hasUnknown
  };
})();
